import { memo } from "react";
import { useAtomValue } from "jotai";
import { ColorPicker, Empty, Button, Tooltip } from "antd";
import { ReloadOutlined, FileTextOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { allCollectFormsMapAtom, availableFormsAtom } from "~/components/Map/panels/GeocollectDatatablePanel/atoms/CollectFormAtoms";
import { getHashColor } from "~/utils/hashStringUtil";
import { LayerStyles, colorToHex } from "../types";

interface FormStyleTabProps {
  styles: LayerStyles;
  onUpdateFormColor: (formUuid: string, color: string) => void;
  onResetFormColors: () => void;
}

const FormStyleTab = ({
  styles,
  onUpdateFormColor,
  onResetFormColors,
}: FormStyleTabProps) => {
  const availableForms = useAtomValue(availableFormsAtom);
  const allFormsMap = useAtomValue(allCollectFormsMapAtom);

  // Combine availableForms and allFormsMap
  const forms = availableForms.length > 0
    ? availableForms
    : Object.values(allFormsMap);

  const getFormDefaultColor = (uuid: string, name?: string) => {
    return getHashColor(name || uuid).backgroundColor;
  };

  const getFormCurrentColor = (uuid: string, name?: string) => {
    return styles.formColors?.[uuid] || getFormDefaultColor(uuid, name);
  };

  return (
    <Container>
      <DescriptionBox>
        <span>
          Mỗi biểu mẫu thu thập sẽ có một màu sắc đặc trưng riêng. Các đối tượng trên bản đồ thuộc về biểu mẫu nào sẽ tự động được tô màu tương ứng.
        </span>
        {forms.length > 0 && (
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={onResetFormColors}
          >
            Đặt lại màu tự động
          </Button>
        )}
      </DescriptionBox>

      {forms.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Chưa có biểu mẫu thu thập trong dự án"
        />
      ) : (
        <FormList>
          {forms.map((form) => {
            if (!form?.uuid) return null;
            const currentColor = getFormCurrentColor(form.uuid, form.name);
            const defaultColor = getFormDefaultColor(form.uuid, form.name);
            const isCustom = styles.formColors?.[form.uuid] && styles.formColors[form.uuid] !== defaultColor;

            return (
              <FormCard key={form.uuid}>
                <FormInfo>
                  <ColorBadge style={{ backgroundColor: currentColor }}>
                    <FileTextOutlined style={{ fontSize: 16 }} />
                  </ColorBadge>
                  <FormDetails>
                    <FormName>{form.name || `Biểu mẫu #${form.uuid.substring(0, 8)}`}</FormName>
                    <FormSub>
                      {form.description || `UUID: ${form.uuid.substring(0, 12)}...`}
                    </FormSub>
                  </FormDetails>
                </FormInfo>

                <ActionsGroup>
                  {isCustom && (
                    <Tooltip title="Khôi phục màu tự động">
                      <Button
                        type="text"
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={() => onUpdateFormColor(form.uuid, defaultColor)}
                      />
                    </Tooltip>
                  )}
                  <ColorPicker
                    value={currentColor}
                    onChange={(color) => onUpdateFormColor(form.uuid, colorToHex(color))}
                    showText
                  />
                </ActionsGroup>
              </FormCard>
            );
          })}
        </FormList>
      )}
    </Container>
  );
};

export default memo(FormStyleTab);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 12px 0;
`;

const DescriptionBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  background-color: #f0fdfa;
  border: 1px solid #99f6e4;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #0f766e;
  line-height: 1.5;
`;

const FormList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 380px;
  overflow-y: auto;
  padding-right: 4px;
`;

const FormCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  transition: all 0.2s;

  &:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
  }
`;

const FormInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const ColorBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
`;

const FormDetails = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const FormName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FormSub = styled.span`
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
